import { Component, OnInit } from '@angular/core';
import { OnDestroy } from '@angular/core/src/metadata/lifecycle_hooks';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingProvider } from '@app/components/_shared/loading-spinner/loading-spinner.component';
import { ModalProvider } from '@app/components/_shared/modal/modal.component';
import { Breadcrumb } from '@app/models/_base/breadcrumb.model';
import { AnswerRequest } from '@app/models/questionnaire/answerRequest.model';
import { AnswerResponse } from '@app/models/questionnaire/answerResponse.model';
import { Question } from '@app/models/questionnaire/question.model';
import { QuestionnaireModel } from '@app/models/questionnaire/questionnaire.model';
import { SubmitAnswersModel } from '@app/models/questionnaire/submitAnswers.model';
import { Footer } from '@app/services/footer.service';
import { LocalisationService } from '@app/services/localisation.service';
import {
  OfflineQuestionnaireService,
  QuestionPost
} from '@app/services/offline-questionnaire.service';
import { QuestionnaireService } from '@app/services/questionnaire.service';
import { RouteBreadcrumbs } from '@app/services/routeBreadcrumbs.service';
import { WindowUtils } from '@app/utils/windowUtils';
import * as _ from 'lodash';
import { Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-questionnaire',
  templateUrl: './questionnaire.component.html',
  styleUrls: ['./questionnaire.component.scss']
})
export class QuestionnaireComponent implements OnInit, OnDestroy {
  public static route: string = 'questions';

  locale: any = {};

  questionnaire: QuestionnaireModel;
  question: Question = new Question();
  questions: Question[];
  defaultQuestion = new Question();

  isReview: boolean;
  questionIndex: number = 0;
  maxIndex: number;
  onQuestionChange: Subject<any> = new Subject();
  onQuestionSubmit: Subject<any> = new Subject();
  lastAnswered: number = 0;

  submitingQuestion: boolean = false;

  questionnaireSubscription: Subscription;

  constructor(
    private routeBreadcrumbs: RouteBreadcrumbs,
    private footerService: Footer,
    private modal: ModalProvider,
    private questionnaireService: QuestionnaireService,
    private router: Router,
    private loading: LoadingProvider,
    private route: ActivatedRoute,
    private localisation: LocalisationService,
    private offlineQuestionnaireService: OfflineQuestionnaireService
  ) {}

  ngOnInit() {
    this.locale = this.localisation.getTranslationsForComponent('QuestionnaireComponent');

    this.loadQuestions();

    this.routeBreadcrumbs.breadcrumbs.next([new Breadcrumb(this.locale.breadcrumbText, null)]);
    this.routeBreadcrumbs.disableBackButton.next(true);

    this.footerService.update(null);

    this.defaultQuestion.type = 'QTYPE_SINGLE';
  }

  ngOnDestroy() {
    this.questionnaireSubscription.unsubscribe();
  }

  loadQuestions() {
    this.loading.show(this.locale.loadingText, true);

    this.route.params.subscribe(
      params => {
        this.isReview = params['review'];
      },
      error => {
        this.loading.hide();
      }
    );

    this.questionnaireSubscription = this.questionnaireService.getCurrentQuestionnaire().subscribe(
      questionnaire => {
        this.questionnaire = questionnaire;
        this.maxIndex = questionnaire.questions - 1;
        this.offlineQuestionnaireService.setSaveLimit(questionnaire.saveLimit);

        this.questionnaireService.getQuestions().subscribe(
          questionsModel => {
            this.questions = questionsModel.questions;

            if (questionnaire.firstIncomplete) {
              this.showQuestion(
                this.offlineQuestionnaireService.firstIncomplete(questionnaire.firstIncomplete)
              );
            } else if (this.isReview) {
              this.showQuestion(_.last(questionsModel.questions).questionId);
            } else {
              this.router.navigate([`/review-questions`]);
            }

            this.loading.hide();
          },
          error => {
            this.loading.hide();
          }
        );
      },
      error => {
        this.loading.hide();
      }
    );
  }

  nextQuestion() {
    if (this.isReview && this.questionIndex >= this.maxIndex) {
      this.router.navigate([`/centigrade/questions/complete`]);
    } else {
      this.onQuestionChange.next(true);
    }
  }

  previousQuestion() {
    this.onQuestionChange.next(false);
  }

  onAnswersSubmitted(model: SubmitAnswersModel) {
    if (!this.submitingQuestion) {
      this.submitingQuestion = true;
      if (!model.directionIsNext || model.answers == null) {
        //Go back without submitting answers
        this.changeQuestion(model.directionIsNext);
        this.submitingQuestion = false;
        return;
      }

      let request = new AnswerRequest();
      request.responses = model.answers;

      let post = { questionId: this.question.questionId, answer: request } as QuestionPost;
      if (this.offlineQuestionnaireService.sendQuestion(post) >= 0) {
        this.offlineQuestionnaireService.trySendAll(() => {
          this.changeQuestion(model.directionIsNext);
          this.onQuestionSubmit.next();
        });
      } else {
        this.answerRequestFailed(null);
        model.answers.forEach(e => (e.selected = false));
        this.onQuestionSubmit.next();
      }

      this.submitingQuestion = false;
    }
  }

  showQuestion(questionIndex: number) {
    let question = _.find(this.questions, (q, index) => {
      this.questionIndex = index;
      return q.questionId === questionIndex;
    });

    this.question = this.defaultQuestion;

    setTimeout(() => {
      this.question = question;
    });
  }

  private changeQuestion(isNext: boolean) {
    let nextIndex = this.questionIndex;

    if (isNext) {
      nextIndex++;
      if (this.lastAnswered < nextIndex) {
        this.lastAnswered = nextIndex;
      }
    } else {
      nextIndex--;
    }

    if (nextIndex > this.maxIndex) {
      //Questionnaire complete
      this.questionnaireService.submitCompleteQuestionnaire().subscribe(
        result => {
          this.router.navigate([`/centigrade/questions/complete`]);
        },
        result => {
          this.showQuestion(result.firstIncomplete);
        }
      );
    } else {
      this.questionIndex = nextIndex;

      this.question = this.defaultQuestion;

      setTimeout(() => {
        this.question = this.questions[this.questionIndex];
      });
    }
    WindowUtils.scrollToY(0, 1500, 'easeInOutQuint');
  }

  retry = () => {
    this.offlineQuestionnaireService.trySendAll(() => {
      if (this.offlineQuestionnaireService.count() === 0) {
        this.changeQuestion(true);
        this.onQuestionSubmit.next();
        this.modal.close();
      }
    });
  };

  private answerRequestFailed(err: AnswerResponse) {
    if (!err) {
      this.modal.create(
        this.locale.noInternetModelTitle,
        this.locale.noInternetModelMessage,
        this.retry,
        this.locale.noInternetModelButtonText
      );
    } else {
      switch (err.returnCode.label) {
        case 'E_NO_ANSWER':
          this.modal.create(this.locale.errorModalTitle, err.returnCode.message);
          break;
        case 'E_INVALID_REQUEST':
          this.modal.create(
            this.locale.errorModalTitle,
            err.returnCode.errors ? err.returnCode.errors.toString() : err.returnCode.message
          );
          break;
        default:
          this.modal.create(this.locale.errorModalTitle, err.returnCode.message);
          break;
      }
    }
  }

  isAnswered(question: Question) {
    return question.answered || question.options.some(e => e.selected);
  }
}



// WEBPACK FOOTER //
// ./src/app/components/questionnaire/questionnaire/questionnaire.component.ts